import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";
import { Search } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function StockReport() {
  const [stockTransactions, setStockTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({
    product_id: "",
    transaction_type: "",
    from_date: "",
    to_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [summary, setSummary] = useState({
    total_in: 0,
    total_out: 0,
    available_qty: 0,
    valuation_amount: 0,
  });
  const [productSummary, setProductSummary] = useState([]);
  const [detailProduct, setDetailProduct] = useState(null);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);

  const authHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
    },
  });

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.product_name || "",
      })),
    [products]
  );

  const selectedProduct = useMemo(
    () =>
      productOptions.find((option) => String(option.value) === String(filter.product_id)) ||
      null,
    [filter.product_id, productOptions]
  );

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, {
        ...authHeader(),
        params: { page: 1, per_page: 200 },
      });
      const data = res.data.data;
      setProducts(data.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const loadStockReport = useCallback(
    async (page = 1, filterParams = null) => {
      const paramsFilter = filterParams || filter;

      try {
        setLoading(true);
        const res = await axios.get(`${API}/reports/stock`, {
          ...authHeader(),
          params: {
            page,
            per_page: pagination.per_page,
            search,
            product_id: paramsFilter.product_id,
            transaction_type: paramsFilter.transaction_type,
            from_date: paramsFilter.from_date,
            to_date: paramsFilter.to_date,
          },
        });

        const data = res.data.data;
        setStockTransactions(data.data || []);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
        setSummary({
          total_in: Number(res.data.summary?.total_in || 0),
          total_out: Number(res.data.summary?.total_out || 0),
          available_qty: Number(res.data.summary?.available_qty || 0),
          valuation_amount: Number(res.data.summary?.valuation_amount || 0),
        });
        setProductSummary(res.data.product_summary || []);
      } catch (error) {
        console.log(error);
        Swal.fire("Error", "Stock report loading failed", "error");
      } finally {
        setLoading(false);
      }
    },
    [authHeader, filter, pagination.per_page]
  );

  useEffect(() => {
    loadProducts();
    loadStockReport(1);
  }, []);

  const handleFilterChange = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    setDetailProduct(null);
    setSelectedProductDetail(null);
    loadStockReport(1);
  };

  const handleProductSelect = (value) => {
    setFilter((prev) => ({ ...prev, product_id: value }));
  };

  const handleTransactionTypeSelect = (value) => {
    setFilter((prev) => ({ ...prev, transaction_type: value }));
  };

  const handleDateChange = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setSearch("");
    setDetailProduct(null);
    setSelectedProductDetail(null);
    const reset = {
      product_id: "",
      transaction_type: "",
      from_date: today(),
      to_date: today(),
    };
    setFilter(reset);
    loadStockReport(1, reset);
  };

  const handleShowDetail = async (productId) => {
    const selected = productSummary.find((item) => String(item.product_id) === String(productId));
    if (!selected) {
      return;
    }

    setSelectedProductDetail(selected);
    setDetailProduct(null);
    setLoading(true);

    try {
      const res = await axios.get(`${API}/reports/stock`, {
        ...authHeader(),
        params: {
          page: 1,
          per_page: pagination.per_page,
          search,
          product_id: selected.product_id,
          transaction_type: filter.transaction_type,
          from_date: filter.from_date,
          to_date: filter.to_date,
        },
      });

      const data = res.data.data;
      setDetailProduct(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Detail transactions loading failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSummary = () => {
    setDetailProduct(null);
    setSelectedProductDetail(null);
  };

  const totals = useMemo(() => {
    return stockTransactions.reduce(
      (sum, row) => {
        sum.qty_in += Number(row.qty_in || 0);
        sum.qty_out += Number(row.qty_out || 0);
        return sum;
      },
      { qty_in: 0, qty_out: 0 }
    );
  }, [stockTransactions]);

  return (
    <AppLayout title="Stock Report" subtitle="View stock transaction history">
      <div className="product-table-page">
        <div className="product-toolbar">
          <div className="product-search-box">
            <Search size={20} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFilterApply();
              }}
              placeholder="Search product name, HSN, remarks"
            />
          </div>

          <div className="sale-date-filter">
            <input
              type="date"
              value={filter.from_date}
              onChange={(e) => handleDateChange("from_date", e.target.value)}
            />
            <input
              type="date"
              value={filter.to_date}
              onChange={(e) => handleDateChange("to_date", e.target.value)}
            />
            <button type="button" className="product-add-btn" onClick={handleFilterApply}>
              Search
            </button>
          </div>
        </div>

        <div className="product-inline-card product-form-only-card stock-filter-card">
          <div className="product-inline-grid stock-filter-grid">
            <div className="eb-field">
              <label>Product</label>
              <Select
                value={selectedProduct}
                options={productOptions}
                onChange={(option) => handleProductSelect(option?.value || "")}
                isClearable
                placeholder="Select product"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 56,
                    borderRadius: 16,
                    borderColor: "#cfe0f7",
                    background: "#f8fbff",
                  }),
                }}
              />
            </div>

            <div className="eb-field">
              <label>Transaction Type</label>
              <select
                name="transaction_type"
                value={filter.transaction_type}
                onChange={(e) => handleTransactionTypeSelect(e.target.value)}
              >
                <option value="">All</option>
                <option value="PURCHASE">Purchase</option>
                <option value="SALE">Sale</option>
                <option value="ADJUSTMENT">Adjustment</option>
              </select>
            </div>

            <div className="product-inline-actions">
              <button type="button" className="product-cancel-btn" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="sale-index-summary">
          <TotalsCard title="Total Qty In" value={summary.total_in} />
          <TotalsCard title="Total Qty Out" value={summary.total_out} />
          <TotalsCard title="Available Qty" value={summary.available_qty} />
          <TotalsCard title="Valuation" value={summary.valuation_amount} currency />
        </div>

        <div className="product-inline-card product-form-only-card stock-filter-card">
          <div className="product-inline-grid stock-filter-grid">
            <div className="eb-field">
              <label>Product Summary</label>
              <div className="summary-note">Click a product name for detail transactions</div>
            </div>
          </div>
        </div>

        <div className="product-table-card">
          <div className="product-table-wrapper">
            {detailProduct && selectedProductDetail ? (
              <div className="stock-detail-header">
                <div>
                  <strong>{selectedProductDetail.product_name}</strong> (
                  {selectedProductDetail.hsn_code || "N/A"})
                </div>
                <button type="button" className="product-cancel-btn" onClick={handleBackToSummary}>
                  Back to summary
                </button>
              </div>
            ) : null}
            <table className="product-simple-table">
              <thead>
                <tr>
                  {detailProduct ? (
                    <>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Type</th>
                      <th>Ref</th>
                      <th>In</th>
                      <th>Out</th>
                      <th>Rate</th>
                      <th>Remarks</th>
                    </>
                  ) : (
                    <>
                      <th>Product</th>
                      <th>HSN</th>
                      <th>Available</th>
                      <th>Sale Rate</th>
                      <th>Valuation</th>
                      <th>Details</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="product-empty-row">
                      Loading stock report...
                    </td>
                  </tr>
                ) : detailProduct ? (
                  detailProduct.map((row) => (
                    <tr key={row.id}>
                      <td>{String(row.transaction_date || "").slice(0, 10)}</td>
                      <td>{row.product_name || "-"}</td>
                      <td>{row.transaction_type || "-"}</td>
                      <td>{`${row.ref_table || ""} ${row.ref_id || ""}`.trim()}</td>
                      <td>{Number(row.qty_in || 0).toFixed(2)}</td>
                      <td>{Number(row.qty_out || 0).toFixed(2)}</td>
                      <td>₹{Number(row.rate || 0).toFixed(2)}</td>
                      <td>{row.remarks || "-"}</td>
                    </tr>
                  ))
                ) : productSummary.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="product-empty-row">
                      No product summary found.
                    </td>
                  </tr>
                ) : (
                  productSummary.map((item) => (
                    <tr key={item.product_id}>
                      <td>
                        <button
                          type="button"
                          className="product-link-button"
                          onClick={() => handleShowDetail(item.product_id)}
                        >
                          {item.product_name || "-"}
                        </button>
                      </td>
                      <td>{item.hsn_code || "-"}</td>
                      <td>{Number(item.available_qty || 0).toFixed(2)}</td>
                      <td>₹{Number(item.sale_rate || 0).toFixed(2)}</td>
                      <td>₹{Number(item.valuation_amount || 0).toFixed(2)}</td>
                      <td>
                        <button
                          type="button"
                          className="product-link-button"
                          onClick={() => handleShowDetail(item.product_id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="product-pagination">
            <span>
              {stockTransactions.length === 0
                ? "0–0"
                : `${(pagination.current_page - 1) * pagination.per_page + 1}–${
                    (pagination.current_page - 1) * pagination.per_page + stockTransactions.length
                  }`} of {pagination.total}
            </span>

            <button
              disabled={pagination.current_page <= 1}
              onClick={() => loadStockReport(pagination.current_page - 1)}
            >
              ‹
            </button>

            <button
              disabled={pagination.current_page >= pagination.last_page}
              onClick={() => loadStockReport(pagination.current_page + 1)}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function TotalsCard({ title, value, currency = false }) {
  return (
    <div className="sale-collection-card">
      <span>{title}</span>
      <b>{currency ? `₹${Number(value || 0).toFixed(2)}` : Number(value || 0).toFixed(2)}</b>
    </div>
  );
}

export default StockReport;
